/**
 * axios setup to use mock service
 */

import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

const axiosServices = axios.create({ baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:3010/' });
import { translateError } from './error-translator';

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { getIsLoggingOut } from 'contexts/FirebaseContext';

axiosServices.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const user = firebase.auth().currentUser;
      if (user) {
        const token = await user.getIdToken();
        localStorage.setItem('serviceToken', token);
        config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        const accessToken = localStorage.getItem('serviceToken');
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
      }
    } catch (e) {
      console.error('Error getting token', e);
    }
    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

axiosServices.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (getIsLoggingOut()) {
        return Promise.reject(error);
      }
      if (!window.location.href.includes('/login')) {
        window.location.pathname = '/login';
      }
    }

    // NEX-1371: the backend now always answers errors as {success:false, code, message}.
    // This used to reject with a spread plain object (or a bare string), which stripped
    // `.response` from the rejected value — every caller reading the standard axios shape
    // (`err.response?.data?.message`/`.code`, ~40+ call sites across the app) silently got
    // `undefined` and fell back to a generic message. Rejecting with the real error object
    // instead keeps `.response.data` intact, while still exposing the translated message
    // (and any extra fields like `code`/`currentAmount`/`expectedAmount` for PRICE_CHANGED)
    // directly on the error too, for the call sites that read it that way.
    const originalData: unknown = (error.response && error.response.data) || null;
    const isObject = typeof originalData === 'object' && originalData !== null;
    // `dataObject` is a typing convenience only — same object, same runtime shape as
    // `originalData` when `isObject` is true. Property reads below stay exactly as loose as
    // the original (`.message`/`.code` truthy-checked, not `typeof`-narrowed to string): the
    // real backend contract (NEX-1371) always sends strings for both, but nothing here should
    // start rejecting a differently-shaped payload that the pre-TS code would have accepted.
    const dataObject = isObject ? (originalData as Record<string, unknown>) : null;
    const rawMessage: unknown = dataObject && dataObject.message ? dataObject.message : typeof originalData === 'string' ? originalData : 'Wrong Services';
    const code = dataObject ? (dataObject.code as string | undefined) : undefined;
    const translatedMessage = translateError(rawMessage, code);

    if (dataObject) {
      Object.assign(error, dataObject, { message: translatedMessage });
      if (error.response) {
        error.response.data = { ...dataObject, message: translatedMessage };
      }
    } else {
      // Error.message is declared `string` in lib.es5.d.ts; translatedMessage is only ever
      // non-string if `rawMessage` itself was (see the comment above) — same as the pre-TS
      // code, which happily assigned whatever fell out of this chain with no type at all.
      error.message = translatedMessage as string;
    }

    return Promise.reject(error);
  }
);

export default axiosServices;

export async function fetcher<T = unknown>(args: string | [string, AxiosRequestConfig?]): Promise<T> {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get<T>(url, { ...config });

  return res.data;
}
